import * as React from 'react'

import { Box, Divider, Text, Title } from '@factorialco/deprecated-design-system'

import Select from 'modules/core/components/Select'
import i18n from 'modules/core/lib/i18n'

import { usePerDiemWizard } from 'modules/expenses/components/expenses/New/PerDiemWizard/form/context'

interface SelectOption {
  label: string
  value: string
}

interface BudgetAndProjectProps {
  budgetOptions: SelectOption[]
  projectOptions: SelectOption[]
  subprojectOptions: SelectOption[]
  loadingBudgets?: boolean
  loadingProjects?: boolean
}

const BudgetAndProject = ({
  budgetOptions,
  projectOptions,
  subprojectOptions,
  loadingBudgets,
  loadingProjects,
}: BudgetAndProjectProps) => {
  const { data, updateData } = usePerDiemWizard()

  const handleBudgetChange = (value?: string) => {
    updateData({ budgetId: value || undefined })
  }

  const handleProjectChange = (value?: string) => {
    updateData({ projectId: value || undefined })
  }

  const handleSubprojectChange = (value?: string) => {
    updateData({ subprojectId: value || undefined })
  }

  const hasBudgets = budgetOptions.length > 0 || loadingBudgets
  const hasProjects = projectOptions.length > 0 || loadingProjects

  // Don't render the section if there are no budgets and no projects
  if (!hasBudgets && !hasProjects) {
    return null
  }

  return (
    <Box flexDirection="column" gap="s16">
      <Box flexDirection="column" gap="s4">
        <Title
          variant="400"
          title={i18n.t('expenses_v2.forms.new_form.blocks.budget_and_project')}
        />
        <Text.Normal size="200" color="grey800">
          {i18n.t('expenses_v2.forms.new_form.blocks.budget_and_project_subtitle')}
        </Text.Normal>
      </Box>

      <Box flexDirection="column" gap="s16">
        {hasBudgets && (
          <Box flexDirection="column" gap="s4">
            <Text.Normal size="200" weight="medium" color="grey1000">
              {i18n.t('budgets.forms.external_resource.budget_field.label')}
            </Text.Normal>
            <Select
              none={i18n.t('budgets.forms.external_resource.budget_field.options.empty')}
              options={budgetOptions}
              value={data.budgetId?.toString()}
              onChange={handleBudgetChange}
              readonly={loadingBudgets}
            />
          </Box>
        )}

        {hasProjects && (
          <>
            <Box flexDirection="column" gap="s4">
              <Text.Normal size="200" weight="medium" color="grey1000">
                {i18n.t('expenses_v2.forms.fields.projects.project')}
              </Text.Normal>
              <Select
                none={i18n.t('common.select')}
                options={projectOptions}
                value={data.projectId?.toString()}
                onChange={handleProjectChange}
                readonly={loadingProjects}
              />
            </Box>

            {subprojectOptions.length > 0 && (
              <Box flexDirection="column" gap="s4">
                <Text.Normal size="200" weight="medium" color="grey1000">
                  {i18n.t('expenses_v2.forms.fields.projects.subproject')}
                </Text.Normal>
                <Select
                  none={i18n.t('common.select')}
                  options={subprojectOptions}
                  value={data.subprojectId?.toString()}
                  onChange={handleSubprojectChange}
                  readonly={loadingProjects || !data.projectId}
                />
              </Box>
            )}
          </>
        )}
      </Box>

      <Box paddingTop="s8" paddingBottom="s8">
        <Divider vertical={false} />
      </Box>
    </Box>
  )
}

export default BudgetAndProject
